import { Text } from '../design/index.js';
import { cx } from '../design/index.js';
import styles from './ChartLegend.module.css';

export interface ChartLegendItem {
  /** What the reader should call it. */
  label: string;
  /**
   * The chart's OWN class for that series — `styles.driftLine`, not a copy of
   * its colour.
   *
   * This is the whole design of this component. A legend that restates a
   * stroke is a second source of truth for it, and the first time somebody
   * changes a dash pattern the key quietly starts lying. Handing the real
   * class over means the swatch is drawn by the same rule as the line, so it
   * cannot drift.
   */
  className: string | undefined;
  /** A band rather than a line: drawn as a filled rectangle. */
  shape?: 'line' | 'band' | 'bar';
  /**
   * Makes this item a switch for its own line.
   *
   * Present on the trajectory chart, where six lines on half a page is more
   * than anyone wants at once; absent on the weekly chart, which has fewer
   * and where hiding one would leave a bar chart with nothing to read
   * against. A legend that also controls is one list rather than two with
   * the same names in them, which is one fewer thing to keep in step.
   */
  toggle?: { checked: boolean; onChange: (next: boolean) => void };
}

export interface ChartLegendProps {
  items: ChartLegendItem[];
  className?: string;
}

/**
 * The key under a chart.
 *
 * Added because two charts ended up side by side carrying five lines between
 * them and the owner could not tell which was which — which was the right
 * complaint: the lines are distinguishable from each other by dash and
 * weight, which is what makes them readable, but distinguishable is not the
 * same as identified.
 *
 * Callers pass only the series they ACTUALLY drew, so a chart with no profile
 * never explains a healthy band that is not there.
 */
export function ChartLegend({ items, className }: ChartLegendProps) {
  if (items.length === 0) return null;
  return (
    <ul className={cx(styles.legend, className)}>
      {items.map((item) => {
        {/* aria-hidden on the swatch: the text beside it is the accessible
            name, and a swatch read out as "image" would only get in the way. */}
        const swatch = (
          <svg className={styles.swatch} viewBox="0 0 18 8" aria-hidden="true">
            {item.shape === 'band' ? (
              <rect className={item.className} x="0" y="1" width="18" height="6" />
            ) : item.shape === 'bar' ? (
              <rect className={item.className} x="6" y="0" width="6" height="8" rx="1.5" />
            ) : (
              <line className={item.className} x1="0" y1="4" x2="18" y2="4" />
            )}
          </svg>
        );

        return (
          <li key={item.label} className={styles.item}>
            {item.toggle ? (
              // A real checkbox, so it is reachable by keyboard and announces
              // its own state. The swatch and label sit inside the label
              // element, which is what makes the whole row the hit target.
              <label
                className={cx(styles.row, styles.switchRow, !item.toggle.checked && styles.off)}
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={item.toggle.checked}
                  onChange={(event) => item.toggle?.onChange(event.target.checked)}
                />
                {swatch}
                <Text as="span" size="2xs" color="muted">
                  {item.label}
                </Text>
              </label>
            ) : (
              <span className={styles.row}>
                {swatch}
                <Text as="span" size="2xs" color="muted">
                  {item.label}
                </Text>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
